// The recovery ladder: given what a navigation has tried so far and why the last try failed, what
// happens next. Pure, so the shell runs it the same way in both modes and the unit tests can walk
// every cause x method x tried-set (test/ladder.test.mjs).
import { isForm } from "./fail-classify.js";

export const LIMITS = {
	extra: 3, // automatic attempts after the first
	loaderMs: 25000, // loader time across all attempts (waits for server/online not counted)
	firstByteMs: 12000, // a retry rung on an in-browser connection gets this long to a first byte
	serverFirstByteMs: 15000, // ... on the Server connection
	serverWaitMs: 180000, // "Reconnecting to Re:Vault" gives up after this
	busyRetries: 1,
};

export const ENGINE_ORDER = ["scramjet2", "scramjet", "uv"];
export const TRANSPORT_ORDER = ["libcurl", "epoxy", "server"];
const IN_BROWSER = ["libcurl", "epoxy"];
const SITE_CARDS = new Set(["site-dns", "site-down", "site-tls", "unknown"]);
// Causes that walk a chain of their own (engines, connections) rather than retrying one thing.
const CHAINS = { engine: 2, "tls-lib": 2 };

export function newNav(start, now, method) {
	return {
		method: method || "GET",
		startedAt: now,
		rungAt: now,
		loaderMs: 0,
		waitMs: 0,
		attempts: [{ engine: start.engine, transport: start.transport, cause: null }],
		busy: 0,
		ended: false,
	};
}
export const current = (nav) => nav.attempts[nav.attempts.length - 1];
export const rung = (nav) => nav.attempts.length - 1;
const tried = (nav, k) => new Set(nav.attempts.map((a) => a[k]));

// ctx: { now, req ({method, phase} | null), avoid ([transport]), engines (engines this page can
//        run, best first), savedTransport, tunnelOk (bool | null), jitter (0..1) }
// Returns one of:
//   { do: "site" }                     the site answered: show its page, nothing to do
//   { do: "card", cause }              stop and show the card for `cause`
//   { do: "retry", engine, transport, delay }   reload this page on that combination
//   { do: "wait", what: "server" | "online" }
//   { do: "hint" }                     slow, not failed: offer "Keep waiting"
//   { do: "handoff" } | { do: "asset" }
export function nextStep(nav, cause, ctx) {
	const c = ctx || {};
	const cur = current(nav);
	const prev = nav.attempts.length > 1 ? nav.attempts[nav.attempts.length - 2] : null;
	cur.cause = cause;
	if (cause === "site-answered") return { do: "site" };
	if (cause === "slow") return { do: "hint" };
	if (cause === "stranded") return { do: "handoff" };
	if (cause === "asset") return { do: "asset" };
	if (SITE_CARDS.has(cause)) return end(nav, cause);
	// A form (any non-GET) is never repeated by itself. Every automatic step reloads the address,
	// which is a GET: it would drop what the student typed without saying so, and if the form did
	// reach the site, only the student can decide to send it twice. The card offers "Send again".
	if (isForm(c.req)) return end(nav, "form");
	if (cause === "offline") return { do: "wait", what: "online" };
	if (cause === "server") return nav.waitMs >= LIMITS.serverWaitMs ? end(nav, "server") : { do: "wait", what: "server" };
	if (rung(nav) >= LIMITS.extra || nav.loaderMs >= LIMITS.loaderMs) return end(nav, cause);
	// The same cause twice in a row ends it, except while walking a chain.
	if (prev && prev.cause === cause && !CHAINS[cause] && cause !== "egress-busy") return end(nav, cause);
	const avoid = new Set(c.avoid || []);
	if (cause === "engine") {
		if (countCause(nav, "engine") > CHAINS.engine) return end(nav, cause);
		const have = tried(nav, "engine");
		const pool = (c.engines && c.engines.length ? c.engines : ENGINE_ORDER).filter((e) => !have.has(e));
		const next = ENGINE_ORDER.slice(ENGINE_ORDER.indexOf(cur.engine) + 1).find((e) => pool.includes(e)) || pool[0];
		return next ? step(nav, next, cur.transport, 0) : end(nav, cause);
	}
	if (cause === "tls-lib") {
		if (countCause(nav, "tls-lib") > CHAINS["tls-lib"]) return end(nav, cause);
		const have = tried(nav, "transport");
		const next = TRANSPORT_ORDER.find((t) => !have.has(t) && (t === "server" || !avoid.has(t)));
		return next ? step(nav, cur.engine, next, 0) : end(nav, cause);
	}
	if (cause === "challenge") {
		if (cur.transport === "server" || tried(nav, "transport").has("server")) return end(nav, cause);
		return step(nav, cur.engine, "server", 0);
	}
	if (cause === "tunnel") {
		if (cur.transport === "server" || tried(nav, "transport").has("server")) return end(nav, cause);
		return step(nav, cur.engine, "server", 0);
	}
	if (cause === "egress-busy") {
		if (nav.busy < LIMITS.busyRetries) { nav.busy++; return step(nav, cur.engine, cur.transport, 1500 + Math.round((c.jitter == null ? Math.random() : c.jitter) * 1000)); }
		// Our server connection is full: back to an in-browser one, if the tunnel is up.
		const back = IN_BROWSER.includes(c.savedTransport) && !avoid.has(c.savedTransport) ? c.savedTransport : IN_BROWSER.find((t) => !avoid.has(t));
		if (c.tunnelOk === false || !back || cur.transport !== "server") return end(nav, cause);
		return step(nav, cur.engine, back, 0);
	}
	return end(nav, cause);
}
function countCause(nav, cause) { return nav.attempts.filter((a) => a.cause === cause).length; }
function step(nav, engine, transport, delay) {
	nav.attempts.push({ engine, transport, cause: null });
	return { do: "retry", engine, transport, delay };
}
function end(nav, cause) {
	nav.ended = true;
	return { do: "card", cause };
}

// How long the current rung may go without a first byte before it counts as failed (0 = no limit:
// the first rung is judged by the slow/stuck watchdog instead).
export function rungDeadline(nav) {
	if (rung(nav) === 0) return 0;
	return current(nav).transport === "server" ? LIMITS.serverFirstByteMs : LIMITS.firstByteMs;
}

// One "Try another way" from the card: the next untried rung, ignoring budgets (the student asked).
export function anotherWay(nav, ctx) {
	const c = ctx || {};
	const cur = current(nav);
	const avoid = new Set(c.avoid || []);
	const tr = tried(nav, "transport");
	const t = TRANSPORT_ORDER.find((x) => !tr.has(x) && (x === "server" || !avoid.has(x)));
	if (t) return { engine: cur.engine, transport: t };
	const en = tried(nav, "engine");
	const e = (c.engines && c.engines.length ? c.engines : ENGINE_ORDER).find((x) => !en.has(x));
	if (e) return { engine: e, transport: cur.transport };
	return null;
}

// The connection a new navigation starts on. saved: the Settings value; session: a connection this
// session already switched to for this site; memory: what worked here before ({ t, ok }), already
// checked for expiry; steer: { avoid: [..], tunnel: "ok"|"degraded"|"down" }; suspect: this page saw
// the tunnel fail in the last minute.
export function startTransport(saved, opts) {
	const o = opts || {};
	const steer = o.steer || {};
	const avoid = new Set(steer.avoid || []);
	if ((steer.tunnel === "down" || o.suspect) && o.serverOk !== false) return { transport: "server", why: "tunnel" };
	let want = o.session || (o.memory && o.memory.t) || saved;
	if (avoid.has(want)) want = IN_BROWSER.find((t) => t !== want && !avoid.has(t)) || saved;
	return { transport: want, why: want !== saved ? (o.session || (o.memory && o.memory.t === want) ? "site" : "steer") : null };
}

// Steer flags as the server publishes them ({ sj2, avoid, tunnel, ttl }). Unknown values are
// dropped, both in-browser connections are never avoided at once, and avoid/tunnel only last ttl
// seconds without a refresh (a client that stops hearing from the server forgets them).
export function normSteer(f, now) {
	const avoid = Array.isArray(f && f.avoid) ? f.avoid.filter((t) => IN_BROWSER.includes(t)).slice(0, 1) : [];
	const tunnel = f && (f.tunnel === "down" || f.tunnel === "degraded") ? f.tunnel : "ok";
	const ttl = Math.max(30, Math.min(3600, Number(f && f.ttl) || 600));
	return { avoid, tunnel, until: (now || 0) + ttl * 1000 };
}
export function liveSteer(s, now) {
	if (!s || !(s.until > now)) return { avoid: [], tunnel: "ok" };
	return { avoid: s.avoid || [], tunnel: s.tunnel || "ok" };
}
